import { defineStore } from 'pinia'
import { DIVISAN_COLORS } from '@/config/palette'
import {
  getCalendariosAgrupados,
  crearCalendariosBulk,
  eliminarCalendario,
  reprogramarActividad,
  getCalendarios,
  getEjecuciones,
  crearEjecucion,
  actualizarActividadEjecucion,
} from '@/utils/api_backend'

// ── Helpers de fecha ─────────────────────────────────────────

function toISODate(fecha) {
  return fecha.toISOString().split('T')[0]
}

function finDeAnio(anio) {
  return new Date(anio, 11, 31)
}

function siguienteFecha(fecha, periodicidad) {
  const d = new Date(fecha)
  switch (periodicidad) {
    case 'SEMANAL':     d.setDate(d.getDate() + 7);         break
    case 'MENSUAL':     d.setMonth(d.getMonth() + 1);       break
    case 'TRIMESTRAL':  d.setMonth(d.getMonth() + 3);       break
    case 'SEMESTRAL':   d.setMonth(d.getMonth() + 6);       break
    case 'ANUAL':       d.setFullYear(d.getFullYear() + 1); break
    default: throw new Error(`Periodicidad desconocida: ${periodicidad}`)
  }
  return d
}

/**
 * Ajusta `fecha` al día de semana `diaObjetivo` (0=dom … 6=sáb)
 * dentro de la misma semana ISO (lun–dom).
 *
 * Si diaObjetivo es 0 (domingo) o 6 (sábado) — el usuario eligió
 * un fin de semana — se mueve al lunes siguiente como fallback.
 *
 * La semana ISO empieza en lunes, por eso el cálculo desplaza
 * desde el lunes de esa semana.
 */
function ajustarAlDiaSemana(fecha, diaObjetivo) {
  const d = new Date(fecha)

  // Fallback: fin de semana → lunes siguiente
  if (diaObjetivo === 0 || diaObjetivo === 6) {
    const dia = d.getDay()
    if (dia === 6) d.setDate(d.getDate() + 2)
    if (dia === 0) d.setDate(d.getDate() + 1)
    return d
  }

  // Lunes de la semana actual (getDay: 0=dom,1=lun…6=sáb)
  const diaSemana = d.getDay()
  const diasDesdeElLunes = diaSemana === 0 ? 6 : diaSemana - 1
  const lunes = new Date(d)
  lunes.setDate(d.getDate() - diasDesdeElLunes)

  // Saltar al día objetivo dentro de esa semana
  // diaObjetivo: 1=lun → +0, 2=mar → +1 … 5=vie → +4
  const offset = diaObjetivo - 1
  lunes.setDate(lunes.getDate() + offset)
  return lunes
}

/**
 * Calcula todas las fechas recurrentes del año para una periodicidad dada,
 * respetando el día de semana de la fecha de inicio como ancla.
 *
 * Ejemplo: fechaInicio = "2026-06-05" (viernes, diaObjetivo = 5)
 *   SEMANAL    → cada 7 días, siempre viernes
 *   MENSUAL    → el viernes de la semana equivalente del mes siguiente
 *   SEMESTRAL  → el viernes de la semana del salto semestral
 *
 * El día ancla se extrae automáticamente de fechaInicio.
 * Si cae en fin de semana, se usa el lunes como fallback.
 */
export function calcularFechasRecurrentes(fechaInicio, periodicidad, anio) {
  const limite = finDeAnio(anio)
  const fechas = []

  const inicio   = new Date(fechaInicio + 'T12:00:00')
  // Día de semana ancla (0=dom … 6=sáb) — extraído de la fecha elegida
  const diaAncla = inicio.getDay()

  // Primera fecha: ajustar al día ancla en la semana de la fecha de inicio
  let cursor = ajustarAlDiaSemana(new Date(inicio), diaAncla)

  while (cursor <= limite) {
    fechas.push(toISODate(cursor))
    // Avanzar por periodicidad y re-anclar al mismo día de semana
    cursor = ajustarAlDiaSemana(siguienteFecha(cursor, periodicidad), diaAncla)
  }
  return fechas
}

// ── Colores por estatus ───────────────────────────────────────
export const COLOR_ESTATUS = {
  PENDIENTE:  { bg: '#d48804', text: '#fff'    },
  EN_PROCESO: { bg: '#1565c0', text: '#fff'    },
  COMPLETADO: { bg: DIVISAN_COLORS.green, text: DIVISAN_COLORS.cream },
  ATRASADO:   { bg: '#cc3636', text: '#fff'    },
}

export const LABEL_PERIODICIDAD = {
  SEMANAL: 'Semanal', MENSUAL: 'Mensual',
  TRIMESTRAL: 'Trimestral', SEMESTRAL: 'Semestral', ANUAL: 'Anual',
}

// Mantenido por retrocompatibilidad con otros módulos que lo importen
export const LABEL_TIPO = {
  CONTROL: 'Control', ELECTROMECANICO: 'Electromecánico',
  HIDRAULICO: 'Hidráulico', MECANICO: 'Mecánico', NEUMATICO: 'Neumático',
}

// ── Severidad de estatus (para calcular el peor de un grupo) ─
const SEVERIDAD = { ATRASADO: 4, EN_PROCESO: 3, PENDIENTE: 2, COMPLETADO: 1 }

function estatusGrupo(calendarios) {
  if (!calendarios?.length) return 'PENDIENTE'
  return calendarios.reduce((peor, cal) => {
    return (SEVERIDAD[cal.estatus] || 0) > (SEVERIDAD[peor] || 0) ? cal.estatus : peor
  }, 'COMPLETADO')
}

// ─────────────────────────────────────────────────────────────
export const useCalendarioStore = defineStore('calendario', {
  state: () => ({
    // grupos[] — cada elemento es un grupo devuelto por /calendarios/agrupados/
    // { grupo_id, maquina, maquina_nombre, fecha_programada,
    //   estatus_grupo, total_rutinas, calendarios[] }
    grupos:     [],
    ejecuciones: {},
    cargando:   false,
    guardando:  false,

    mesActual:          '',
    filtroMaquina:      null,
    filtroPeriodicidad: '',   // reemplaza filtroTipo
    filtroEstatus:      '',
    // filtroTipo se conserva como alias vacío para no romper imports existentes
    filtroTipo:         '',

    progresoRecurrente: {
      activo:  false,
      total:   0,
      creados: 0,
      errores: [],
    },
  }),

  getters: {
    // Grupos filtrados en cliente (máquina y estatus — periodicidad ya viene del backend)
    gruposFiltrados: (state) => {
      return state.grupos.filter(g => {
        const matchMaquina = !state.filtroMaquina ||
          Number(g.maquina) === Number(state.filtroMaquina)
        const matchEstatus = !state.filtroEstatus ||
          g.estatus_grupo === state.filtroEstatus
        return matchMaquina && matchEstatus
      })
    },

    // Vista plana de calendarios individuales (compatibilidad con componentes existentes)
    eventosFiltrados: (state) => {
      return state.gruposFiltrados.flatMap(g => g.calendarios)
    },

    // Un evento de FullCalendar por grupo
    eventosFullCalendar: (state) => {
      return state.gruposFiltrados.map(g => {
        const color = COLOR_ESTATUS[g.estatus_grupo] || COLOR_ESTATUS.PENDIENTE
        const titulo = g.total_rutinas > 1
          ? `${g.maquina_nombre} · ${g.total_rutinas} rutinas`
          : `${g.maquina_nombre} · ${g.calendarios[0]?.rutina_nombre || ''}`
        return {
          id:              g.grupo_id,
          title:           titulo,
          start:           g.fecha_programada,
          backgroundColor: color.bg,
          borderColor:     color.bg,
          textColor:       color.text,
          extendedProps:   { ...g, _esGrupo: true },
        }
      })
    },

    proximosEventos: (state) => {
      const hoy = toISODate(new Date())
      return state.grupos
        .flatMap(g => g.calendarios.map(c => ({ ...c, maquina_nombre: g.maquina_nombre })))
        .filter(c => c.estatus !== 'COMPLETADO' && c.fecha_programada >= hoy)
        .sort((a, b) => a.fecha_programada.localeCompare(b.fecha_programada))
        .slice(0, 5)
    },

    conteoEstatus: (state) => {
      return state.grupos.reduce((acc, g) => {
        acc[g.estatus_grupo] = (acc[g.estatus_grupo] || 0) + 1
        return acc
      }, {})
    },
  },

  actions: {
    inicializar() {
      const ahora = new Date()
      this.mesActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`
    },

    async cargarMes(mes = null) {
      if (mes) this.mesActual = mes
      this.cargando = true
      try {
        const params = { mes: this.mesActual }
        if (this.filtroPeriodicidad) params.periodicidad = this.filtroPeriodicidad
        // maquina y estatus se filtran en cliente para no perder contadores totales
        this.grupos = await getCalendariosAgrupados(params)
      } catch (err) {
        console.error('Error cargando calendario agrupado:', err)
        throw err
      } finally {
        this.cargando = false
      }
    },

    async mesAnterior() {
      const [anio, mes] = this.mesActual.split('-').map(Number)
      const fecha = new Date(anio, mes - 2, 1)
      await this.cargarMes(`${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`)
    },

    async mesSiguiente() {
      const [anio, mes] = this.mesActual.split('-').map(Number)
      const fecha = new Date(anio, mes, 1)
      await this.cargarMes(`${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`)
    },

    async irAHoy() {
      const ahora = new Date()
      await this.cargarMes(`${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`)
    },

    setFiltroMaquina(maquinaId) {
      this.filtroMaquina = maquinaId ? Number(maquinaId) : null
      // No recarga — el filtro de máquina se aplica en cliente para conservar contadores
    },

    setFiltroPeriodicidad(periodicidad) {
      this.filtroPeriodicidad = periodicidad || ''
      this.cargarMes()  // periodicidad filtra en backend — necesita recarga
    },

    // Alias vacío — mantiene compatibilidad con BarraControles/PanelLateral que aún lo llamen
    setFiltroTipo() {},

    toggleFiltroEstatus(estatus) {
      this.filtroEstatus = this.filtroEstatus === estatus ? '' : estatus
    },

    // ── Crear eventos para un día — 1..N rutinas ─────────────
    async crearEventosBulk(maquinaId, rutinaIds, fechaProgramada) {
      this.guardando = true
      try {
        const resultado = await crearCalendariosBulk({
          maquina:          maquinaId,
          rutinas:          Array.isArray(rutinaIds) ? rutinaIds : [rutinaIds],
          fecha_programada: fechaProgramada,
        })
        await this.cargarMes()
        return resultado
      } finally {
        this.guardando = false
      }
    },

    // Alias retrocompatible para ModalCrearEvento antiguo
    async crearEvento(maquinaId, rutinaId, _rutina, fechaProgramada) {
      return this.crearEventosBulk(maquinaId, [rutinaId], fechaProgramada)
    },

    async eliminarEvento(id) {
      await eliminarCalendario(id)
      await this.cargarMes()
    },

    // ── Programar recurrente — 1..N rutinas ──────────────────
    // rutinaIds: number[]
    // rutinas:   objeto[] con { id, periodicidad, nombre }
    async programarRecurrente(maquinaId, rutinaIds, rutinas, fechaInicio) {
      const anioActual = new Date().getFullYear()
      const ids = Array.isArray(rutinaIds) ? rutinaIds : [rutinaIds]
      const rutObjetos = Array.isArray(rutinas) ? rutinas : [rutinas]

      // Cargar todos los calendarios del año para esta máquina
      let eventosDelAnio = []
      try {
        const raw = await getCalendarios({ maquina: maquinaId })
        eventosDelAnio = raw.filter(ev =>
          ids.some(id => Number(id) === Number(ev.rutina))
        )
      } catch {
        eventosDelAnio = this.grupos.flatMap(g => g.calendarios)
      }

      // Por cada rutina calcular sus fechas y filtrar conflictos
      // Agrupar resultado en Map<fecha, Set<rutinaId>>
      const fechaARutinas = new Map()
      let totalFechas = 0
      let totalConflictos = 0

      for (const rut of rutObjetos) {
        const fechas = calcularFechasRecurrentes(fechaInicio, rut.periodicidad, anioActual)
        totalFechas += fechas.length
        for (const fecha of fechas) {
          const conflicto = this._buscarConflictoPeriodo(
            maquinaId, rut.id, rut.periodicidad, fecha, eventosDelAnio
          )
          if (conflicto) { totalConflictos++; continue }
          if (!fechaARutinas.has(fecha)) fechaARutinas.set(fecha, [])
          fechaARutinas.get(fecha).push(rut.id)
        }
      }

      const fechasOrdenadas = [...fechaARutinas.keys()].sort()
      const totalNuevas = fechasOrdenadas.reduce((s, f) => s + fechaARutinas.get(f).length, 0)

      this.progresoRecurrente = { activo: true, total: totalNuevas, creados: 0, errores: [] }

      let creados = 0
      const errores = []

      for (const fecha of fechasOrdenadas) {
        try {
          const res = await crearCalendariosBulk({
            maquina:          maquinaId,
            rutinas:          fechaARutinas.get(fecha),
            fecha_programada: fecha,
          })
          creados += res.creados?.length || 0
          totalConflictos += res.omitidos?.length || 0
          this.progresoRecurrente.creados = creados
        } catch (err) {
          if (err?.response?.status !== 400) {
            fechaARutinas.get(fecha).forEach(() => errores.push(fecha))
            this.progresoRecurrente.errores = [...errores]
          }
        }
      }

      this.progresoRecurrente.activo  = false
      this.progresoRecurrente.creados = creados
      await this.cargarMes()
      return { creados, yaExistian: totalConflictos, errores, total: totalFechas }
    },

    // ── Helper: detectar conflicto de período ─────────────────
    _buscarConflictoPeriodo(maquinaId, rutinaId, periodicidad, fechaStr, pool = null) {
      if (!periodicidad) return null
      const fuente = pool ?? this.grupos.flatMap(g => g.calendarios)
      const fecha  = new Date(fechaStr + 'T12:00:00')
      const anio   = fecha.getFullYear()
      const mes    = fecha.getMonth()
      const semana = this._semanaDelAnio(fecha)

      return fuente.find(ev => {
        if (Number(ev.maquina) !== Number(maquinaId)) return false
        if (Number(ev.rutina)  !== Number(rutinaId))  return false
        const evFecha  = new Date(ev.fecha_programada + 'T12:00:00')
        const evAnio   = evFecha.getFullYear()
        const evMes    = evFecha.getMonth()
        const evSemana = this._semanaDelAnio(evFecha)
        switch (periodicidad) {
          case 'SEMANAL':     return evAnio === anio && evSemana === semana
          case 'MENSUAL':     return evAnio === anio && evMes === mes
          case 'TRIMESTRAL':  return evAnio === anio && Math.floor(evMes / 3) === Math.floor(mes / 3)
          case 'SEMESTRAL':   return evAnio === anio && Math.floor(evMes / 6) === Math.floor(mes / 6)
          case 'ANUAL':       return evAnio === anio
          default:            return false
        }
      }) || null
    },

    _semanaDelAnio(fecha) {
      const d = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
    },

    // ── Ejecuciones ───────────────────────────────────────────
    async cargarEjecucion(calendarioId) {
      try {
        const lista = await getEjecuciones({ calendario: calendarioId })
        if (lista.length > 0) this.ejecuciones[calendarioId] = lista[0]
        return this.ejecuciones[calendarioId] || null
      } catch { return null }
    },

    async crearEjecucionParaCalendario(calendarioId, maquinaId, rutinaId) {
      const ejecucion = await crearEjecucion({
        calendario:   calendarioId,
        maquina:      maquinaId,
        rutina:       rutinaId,
        fecha_inicio: new Date().toISOString(),
      })
      this.ejecuciones[calendarioId] = ejecucion
      await this.cargarMes()
      return ejecucion
    },

    async completarActividad(ejecucionId, actividadId, completado, observaciones = '') {
      await actualizarActividadEjecucion(ejecucionId, actividadId, { completado, observaciones })
      const calendarioId = Object.keys(this.ejecuciones).find(
        k => this.ejecuciones[k]?.id === ejecucionId
      )
      if (calendarioId) {
        const lista = await getEjecuciones({ calendario: calendarioId })
        if (lista.length > 0) this.ejecuciones[calendarioId] = lista[0]
      }
      await this.cargarMes()
    },

    async reprogramarActividadCalendario(calendarioId, actividadId, nuevaFecha) {
      await reprogramarActividad(calendarioId, actividadId, nuevaFecha)
      await this.cargarMes()
    },
  },
})